<template>
  <section class="material-card">
    <div class="filter-row">
      <a-input
        v-model:value="filters.title"
        allow-clear
        placeholder="Tab 名称"
        class="filter-control"
      />
      <a-select
        v-model:value="filters.materialType"
        allow-clear
        placeholder="全部类型"
        class="filter-control"
      >
        <a-select-option :value="1">icon</a-select-option>
        <a-select-option :value="2">大图</a-select-option>
        <a-select-option :value="3">icon+文字</a-select-option>
      </a-select>
      <a-select
        v-model:value="filters.status"
        allow-clear
        placeholder="全部状态"
        class="filter-control"
      >
        <a-select-option :value="1">已启用</a-select-option>
        <a-select-option :value="0">已停用</a-select-option>
      </a-select>
      <a-button @click="resetFilters">重置</a-button>
      <a-button type="primary" @click="handleQuery">查询</a-button>
      <a-button type="primary" class="add-button" @click="openCreateModal">
        + 新增 Tab
      </a-button>
    </div>

    <a-table
      row-key="materialNo"
      :columns="columns"
      :data-source="items"
      :loading="loading"
      :pagination="false"
      :scroll="{ x: 1050 }"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.dataIndex === 'languages'">
          <div class="name-cell">
            <strong>{{ getMaterialName(record) }}</strong>
            <a-tag
              v-if="record.configured"
              color="orange"
              class="configured-tag"
            >
              栏位中
            </a-tag>
          </div>
        </template>
        <template v-else-if="column.dataIndex === 'materialType'">
          <a-tag :color="record.materialType === 2 ? 'gold' : 'default'">
            {{ getMaterialTypeName(record) }}
          </a-tag>
        </template>
        <template v-else-if="column.dataIndex === 'jumpType'">
          {{ getJumpTarget(record) }}
        </template>
        <template v-else-if="column.dataIndex === 'status'">
          <a-tag :color="record.status === 1 ? 'green' : 'default'">
            {{ getStatusName(record) }}
          </a-tag>
        </template>
        <template v-else-if="column.dataIndex === 'actions'">
          <div class="table-actions">
            <span class="table-action" @click="openEditModal(record)">
              编辑
            </span>
            <span
              v-if="record.status !== 1 || !record.configured"
              class="table-action"
              :class="{
                disabled: Boolean(changingMaterialNo || deletingMaterialNo)
              }"
              @click="handleChangeStatus(record)"
            >
              {{
                changingMaterialNo === record.materialNo
                  ? '处理中...'
                  : record.status === 1
                  ? '停用'
                  : '启用'
              }}
            </span>
            <a-popconfirm
              v-if="record.status === 0"
              title="确认删除该物料吗？"
              ok-text="确定"
              cancel-text="取消"
              @confirm="handleDelete(record)"
            >
              <span
                class="table-action danger"
                :class="{ disabled: Boolean(deletingMaterialNo) }"
              >
                {{
                  deletingMaterialNo === record.materialNo
                    ? '删除中...'
                    : '删除'
                }}
              </span>
            </a-popconfirm>
          </div>
        </template>
      </template>
    </a-table>

    <div class="pagination-row">
      <span>共 {{ pagination.total }} 条</span>
      <a-pagination
        :current="pagination.pageNum"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :show-size-changer="true"
        :page-size-options="['10', '20', '50', '100']"
        @change="handlePageChange"
      />
    </div>

    <TabFormModal
      v-model:visible="modalVisible"
      :mode="modalMode"
      :record="editingRecord"
      :languages="languageOptions"
      :confirm-loading="submitLoading"
      @submit="handleSubmit"
    />
  </section>
</template>

<script>
import { reactive, ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import { common_lang_get } from '@/server/modules/3426.js'
import {
  appTab_material_changeStatus_post,
  appTab_material_create_post,
  appTab_material_delete_post,
  appTab_material_page_post,
  appTab_material_update_post
} from '@/server/modules/25824.js'
import TabFormModal from './TabFormModal.vue'

const materialTypeNames = {
  1: 'icon',
  2: '大图',
  3: 'icon+文字'
}

const createFilters = () => ({
  title: '',
  materialType: undefined,
  status: undefined
})

export default {
  components: {
    TabFormModal
  },
  props: {
    siteId: {
      type: [Number, String],
      default: undefined
    }
  },
  setup(props) {
    const filters = reactive(createFilters())
    const items = ref([])
    const loading = ref(false)
    const pagination = reactive({
      pageNum: 1,
      pageSize: 20,
      pages: 0,
      total: 0
    })
    const modalVisible = ref(false)
    const modalMode = ref('create')
    const editingRecord = ref(null)
    const submitLoading = ref(false)
    const changingMaterialNo = ref('')
    const deletingMaterialNo = ref('')
    const languageLoading = ref(false)
    const languageOptions = ref([])
    const columns = [
      { title: 'ID', dataIndex: 'materialNo', width: 130 },
      { title: '名称', dataIndex: 'languages', width: 160 },
      { title: '类型', dataIndex: 'materialType', width: 120 },
      { title: '路由', dataIndex: 'jumpType', width: 250 },
      { title: '状态', dataIndex: 'status', width: 100 },
      { title: '更新人', dataIndex: 'updateUser', width: 120 },
      { title: '更新时间', dataIndex: 'lastUpdateTime', width: 170 },
      { title: '操作', dataIndex: 'actions', width: 150, fixed: 'right' }
    ]

    const getRequestParams = () => ({
      siteId: props.siteId,
      materialType: filters.materialType,
      pageNum: pagination.pageNum,
      pageSize: pagination.pageSize,
      status: filters.status,
      title: filters.title.trim()
    })
    const loadMaterialList = async () => {
      if (!props.siteId) return

      loading.value = true
      try {
        const { success, data, retInfo } = await appTab_material_page_post(
          getRequestParams()
        )
        if (!success) {
          message.error(retInfo || '物料列表获取失败')
          return
        }

        items.value = Array.isArray(data?.list) ? data.list : []
        Object.assign(pagination, {
          pageNum: Number(data?.pageNum) || 1,
          pageSize: Number(data?.pageSize) || 20,
          pages: Number(data?.pages) || 0,
          total: Number(data?.total) || 0
        })
      } catch (error) {
        message.error(error?.message || '物料列表获取失败')
      } finally {
        loading.value = false
      }
    }
    const handleQuery = async () => {
      pagination.pageNum = 1
      await loadMaterialList()
    }
    const resetFilters = async () => {
      Object.assign(filters, createFilters())
      pagination.pageNum = 1
      await loadMaterialList()
    }
    const handlePageChange = async (pageNum, pageSize) => {
      pagination.pageNum = pageNum
      pagination.pageSize = pageSize
      await loadMaterialList()
    }
    const getMaterialName = record => {
      const language = Array.isArray(record.languages)
        ? record.languages[0]
        : null
      return (
        language?.materialName ||
        language?.name ||
        language?.title ||
        record.materialNo ||
        '-'
      )
    }
    const getMaterialTypeName = record =>
      record.materialTypeName || materialTypeNames[record.materialType] || '-'
    const getRouteTarget = record =>
      record.routeName || record.routeUrl || record.routeId || '-'
    const getJumpTarget = record =>
      record.jumpType === 1
        ? record.h5Url || '-'
        : `路由 · ${getRouteTarget(record)}`
    const getStatusName = record =>
      record.statusName || (record.status === 1 ? '已启用' : '已停用')
    const handleChangeStatus = async record => {
      if (changingMaterialNo.value || deletingMaterialNo.value) return

      const status = record.status === 1 ? 0 : 1
      changingMaterialNo.value = record.materialNo
      try {
        const { success, retInfo } = await appTab_material_changeStatus_post({
          materialNo: record.materialNo,
          status
        })
        if (!success) {
          message.error(retInfo || `${status === 1 ? '启用' : '停用'}物料失败`)
          return
        }

        message.success(`${status === 1 ? '启用' : '停用'}物料成功`)
        await loadMaterialList()
      } catch (error) {
        message.error(
          error?.message || `${status === 1 ? '启用' : '停用'}物料失败`
        )
      } finally {
        changingMaterialNo.value = ''
      }
    }
    const handleDelete = async record => {
      if (deletingMaterialNo.value || changingMaterialNo.value) return

      deletingMaterialNo.value = record.materialNo
      try {
        const { success, retInfo } = await appTab_material_delete_post({
          materialNo: record.materialNo
        })
        if (!success) {
          message.error(retInfo || '删除物料失败')
          return
        }

        message.success('删除物料成功')
        if (items.value.length === 1 && pagination.pageNum > 1) {
          pagination.pageNum -= 1
        }
        await loadMaterialList()
      } catch (error) {
        message.error(error?.message || '删除物料失败')
      } finally {
        deletingMaterialNo.value = ''
      }
    }

    const loadLanguageOptions = async () => {
      if (!props.siteId) {
        message.warning('请先选择站点')
        return false
      }

      languageLoading.value = true
      try {
        const { success, data, retInfo } = await common_lang_get({
          brandId: 1,
          channelId: 1,
          siteId: props.siteId,
          terminalId: 2
        })
        if (!success) {
          message.error(retInfo || '站点语言获取失败')
          return false
        }

        languageOptions.value = Object.entries(
          data?.languageMap || {}
        ).map(([label, value]) => ({ label, value }))
        if (!languageOptions.value.length) {
          message.warning('当前站点暂无可用语言')
          return false
        }
        return true
      } catch (error) {
        message.error(error?.message || '站点语言获取失败')
        return false
      } finally {
        languageLoading.value = false
      }
    }
    const openModal = async (mode, record = null) => {
      if (languageLoading.value) return
      const languageReady = await loadLanguageOptions()
      if (!languageReady) return

      modalMode.value = mode
      editingRecord.value = record
      modalVisible.value = true
    }
    const openCreateModal = () => openModal('create')
    const openEditModal = record => openModal('edit', record)
    const handleSubmit = async form => {
      const isEdit = modalMode.value === 'edit'
      const actionText = isEdit ? '编辑' : '新建'
      const request = isEdit
        ? appTab_material_update_post
        : appTab_material_create_post
      submitLoading.value = true
      try {
        const { success, retInfo } = await request({
          ...form,
          siteId: Number(props.siteId),
          ...(isEdit ? { materialNo: editingRecord.value?.materialNo } : {})
        })
        if (!success) {
          message.error(retInfo || `${actionText}物料失败`)
          return
        }

        message.success(`${actionText}物料成功`)
        modalVisible.value = false
        if (!isEdit) pagination.pageNum = 1
        await loadMaterialList()
      } catch (error) {
        message.error(error?.message || `${actionText}物料失败`)
      } finally {
        submitLoading.value = false
      }
    }

    watch(
      () => props.siteId,
      async siteId => {
        Object.assign(filters, createFilters())
        Object.assign(pagination, {
          pageNum: 1,
          pageSize: 20,
          pages: 0,
          total: 0
        })
        items.value = []
        modalVisible.value = false
        editingRecord.value = null
        submitLoading.value = false
        changingMaterialNo.value = ''
        deletingMaterialNo.value = ''
        languageOptions.value = []
        if (siteId) await loadMaterialList()
      },
      { immediate: true }
    )

    return {
      columns,
      changingMaterialNo,
      deletingMaterialNo,
      editingRecord,
      filters,
      getJumpTarget,
      getMaterialName,
      getMaterialTypeName,
      getStatusName,
      handleChangeStatus,
      handleDelete,
      handlePageChange,
      handleQuery,
      handleSubmit,
      items,
      languageOptions,
      loadMaterialList,
      loading,
      modalMode,
      modalVisible,
      openCreateModal,
      openEditModal,
      pagination,
      resetFilters,
      submitLoading
    }
  }
}
</script>

<style lang="scss" scoped>
.material-card {
  overflow: hidden;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  background: #fff;
}

.filter-row {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.filter-control {
  width: 180px;
}

.add-button {
  margin-left: auto;
}

.name-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.configured-tag {
  margin-right: 0;
}

.table-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
}

.table-action {
  color: #1890ff;
  cursor: pointer;

  &:hover {
    color: #40a9ff;
  }

  &.danger {
    color: #ff4d4f;

    &:hover {
      color: #ff7875;
    }
  }

  &.disabled {
    color: #bfbfbf;
    cursor: not-allowed;
  }
}

.pagination-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 16px;
  color: #8c8c8c;
}
</style>
